/* eslint-disable react/prop-types */
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import Box from "@mui/material/Box";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

import Sidebar, { SIDEBAR_WIDTH } from "./Sidebar";
import TopBar from "./TopBar";

/**
 * Replacement for `examples/LayoutContainers/DashboardLayout`.
 *
 * - Permanent sidebar on desktop, temporary drawer on mobile (hamburger).
 * - Sticky TopBar with search + user menu.
 * - Page content area fills the rest with consistent padding.
 *
 * Mounted from App.js. Children = the page component.
 */
export default function DashboardShell({ routes = [], children }) {
  const theme = useTheme();
  const desktop = useMediaQuery(theme.breakpoints.up("md"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();

  // Scroll back to top on route change (parity with the old layout).
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
    if (!desktop) setMobileOpen(false);
  }, [pathname, desktop]);

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "background.default" }}>
      <Sidebar
        routes={routes}
        variant={desktop ? "permanent" : "temporary"}
        open={desktop || mobileOpen}
        onClose={() => setMobileOpen(false)}
      />

      <Box
        component="main"
        sx={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          // Leave space for the permanent drawer on desktop.
          ml: { xs: 0, md: `0` }, // Drawer width is already taken up by the flex sidebar
        }}
      >
        <TopBar onMenuClick={() => setMobileOpen(true)} />
        <Box
          id="printable-content"
          sx={{
            flex: 1,
            px: { xs: 2, md: 3.5 },
            py: { xs: 2, md: 3 },
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
}

export { SIDEBAR_WIDTH };
